import { COOKIE_EXPIRY_DAYS, MAX_RETRIES, RETRY_BASE_DELAY } from './constants.js';

export const CookieHelper = {
  get(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const raw = c.substring(nameEQ.length, c.length);
        try {
          return decodeURIComponent(raw);
        } catch (_) {
          // Legacy cookies written before encoding was introduced.
          return raw;
        }
      }
    }
    return null;
  },

  set(name, value, days = COOKIE_EXPIRY_DAYS) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    const encoded = encodeURIComponent(value === null || value === undefined ? '' : String(value));
    document.cookie =
      name + '=' + encoded + expires + '; path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
  },

  delete(name) {
    document.cookie =
      name +
      '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }
};

export const UUIDHelper = {
  generate() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = [];
      for (let i = 0; i < 16; i++) {
        hex.push((bytes[i] + 0x100).toString(16).slice(1));
      }
      return (
        hex[0] +
        hex[1] +
        hex[2] +
        hex[3] +
        '-' +
        hex[4] +
        hex[5] +
        '-' +
        hex[6] +
        hex[7] +
        '-' +
        hex[8] +
        hex[9] +
        '-' +
        hex[10] +
        hex[11] +
        hex[12] +
        hex[13] +
        hex[14] +
        hex[15]
      );
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
};

export const SecurityHelper = {
  escapeHTML(str) {
    if (!str) return '';
    const d = document.createElement('div');
    d.textContent = String(str);
    return d.innerHTML;
  },

  escapeAttribute(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  },

  sanitizeURL(url) {
    if (!url || typeof url !== 'string') return '';
    const u = url.trim();
    if (/^(?:https?|mailto):/i.test(u)) return u;
    // Allow image data URLs: used by the live dashboard preview to render
    // freshly-uploaded assets (avatars/icons) before they are saved. Safe as
    // an <img src> — SVG scripts do not execute in image context.
    if (/^data:image\//i.test(u)) return u;
    if (/^[a-z][\w+.-]*:/i.test(u)) return '';
    return u;
  },

  sanitizeHTML(html) {
    if (!html || typeof html !== 'string') return '';

    const ALLOWED = new Set([
      'a',
      'b',
      'strong',
      'em',
      'i',
      'u',
      'br',
      'p',
      'ul',
      'ol',
      'li',
      'div',
      'span',
      'img',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      'blockquote',
      'code',
      'pre',
      'sub',
      'sup',
      'small',
      'table',
      'thead',
      'tbody',
      'tr',
      'td',
      'th'
    ]);
    const STRIP = new Set([
      'script',
      'style',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'textarea',
      'select',
      'link',
      'meta',
      'base',
      'svg',
      'math',
      'template',
      'noscript'
    ]);
    const SAFE_ATTRS = {
      a: ['href', 'target', 'rel', 'title'],
      img: ['src', 'alt', 'width', 'height'],
      '*': ['class']
    };

    const doc = new DOMParser().parseFromString(html, 'text/html');

    function clean(parent) {
      for (const node of Array.from(parent.childNodes)) {
        if (node.nodeType === Node.COMMENT_NODE) {
          node.remove();
          continue;
        }
        if (node.nodeType === Node.TEXT_NODE) continue;
        if (node.nodeType !== Node.ELEMENT_NODE) {
          node.remove();
          continue;
        }

        const tag = node.tagName.toLowerCase();

        if (STRIP.has(tag)) {
          node.remove();
          continue;
        }

        if (!ALLOWED.has(tag)) {
          while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
          node.remove();
          clean(parent);
          return;
        }

        const tagAllowed = SAFE_ATTRS[tag] || [];
        const globalAllowed = SAFE_ATTRS['*'] || [];
        const allowed = new Set([...tagAllowed, ...globalAllowed]);

        for (const attr of Array.from(node.attributes)) {
          const name = attr.name.toLowerCase();
          if (name.startsWith('on') || !allowed.has(name)) {
            node.removeAttribute(attr.name);
            continue;
          }
          if (name === 'href' || name === 'src') {
            const v = attr.value.replace(/[\s\x00-\x1f]/g, '').toLowerCase();
            if (/^(?:javascript|data|vbscript):/i.test(v)) {
              node.removeAttribute(attr.name);
            }
          }
        }

        if (tag === 'a' && node.hasAttribute('href')) {
          node.setAttribute('rel', 'noopener noreferrer');
        }

        clean(node);
      }
    }

    clean(doc.body);
    return doc.body.innerHTML;
  }
};

export const StorageHelper = {
  get(key) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      console.error('Erreur lecture localStorage:', e);
      return null;
    }
  },

  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error('Erreur écriture localStorage:', e);
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Erreur suppression localStorage:', e);
    }
  }
};

export async function fetchWithRetry(url, options = {}, maxRetries = MAX_RETRIES, baseDelay = RETRY_BASE_DELAY) {
  const signal = options && options.signal;
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal && signal.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      throw err;
    }
    try {
      const response = await fetch(url, options);
      if (response.ok || attempt === maxRetries) {
        return response;
      }
      if (response.status >= 400 && response.status < 500) {
        return response;
      }
    } catch (error) {
      lastError = error;
      // Do not retry if the caller aborted the request.
      if (error && (error.name === 'AbortError' || (signal && signal.aborted))) {
        throw error;
      }
      if (attempt === maxRetries) {
        throw lastError;
      }
    }
    const delay = baseDelay * Math.pow(2, attempt);
    await abortableDelay(delay, signal);
  }
  throw lastError;
}

function abortableDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      const err = new Error('Aborted');
      err.name = 'AbortError';
      reject(err);
      return;
    }
    const timer = setTimeout(() => {
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      const err = new Error('Aborted');
      err.name = 'AbortError';
      reject(err);
    }
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Classify an error or response into a stable code consumable by i18n.
 * Returns one of: 'timeout' | 'offline' | 'rate_limited' | 'auth' | 'server' | 'aborted' | 'unknown'.
 */
export function classifyNetworkError(error, response) {
  if (error) {
    if (error.name === 'AbortError') return 'aborted';
    if (error.name === 'TimeoutError') return 'timeout';
    // fetch() throws a TypeError for network failures (DNS, offline, CORS).
    if (error.name === 'TypeError') return 'offline';
  }
  if (response && typeof response.status === 'number') {
    if (response.status === 401 || response.status === 403) return 'auth';
    if (response.status === 408) return 'timeout';
    if (response.status === 429) return 'rate_limited';
    if (response.status >= 500 && response.status < 600) return 'server';
  }
  return 'unknown';
}

export function formatMessageTime(isoString, lang = 'fr') {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' }).format(date);
  } catch (e) {
    return '';
  }
}

/**
 * Format a timestamp as a short, localised relative label ("il y a 6 min",
 * "3 h", "2 j"). Falls back to an absolute day/month for anything older than a
 * week. Used by the conversations list to mirror the "6m ago" style preview.
 */
export function formatRelativeTime(isoString, lang = 'fr', now = Date.now()) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const diffMs = date.getTime() - now;
    const absSec = Math.abs(diffMs) / 1000;
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    if (absSec < 60) return rtf.format(Math.round(diffMs / 1000), 'second');
    if (absSec < 3600) return rtf.format(Math.round(diffMs / 60000), 'minute');
    if (absSec < 86400) return rtf.format(Math.round(diffMs / 3600000), 'hour');
    if (absSec < 604800) return rtf.format(Math.round(diffMs / 86400000), 'day');
    return new Intl.DateTimeFormat(lang, { day: '2-digit', month: 'short' }).format(date);
  } catch (e) {
    return '';
  }
}

/**
 * Strip HTML tags and collapse whitespace into a short plain-text snippet.
 * Used to build conversation previews from stored message content (which may
 * be sanitised HTML for bot replies). Never produces markup.
 */
export function stripHtml(html, maxLength = 60) {
  if (!html || typeof html !== 'string') return '';
  let text = html.replace(/<[^>]*>/g, ' ');
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"');
  text = text.replace(/\s+/g, ' ').trim();
  if (maxLength > 0 && text.length > maxLength) {
    text = text.slice(0, maxLength).trimEnd() + '…';
  }
  return text;
}

/**
 * Return relative luminance of an RGB color per WCAG 2.1.
 * `r`, `g`, `b` are 0-255 channels.
 */
export function relativeLuminance(r, g, b) {
  const toLinear = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function parseHexColor(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16)
  };
}

/**
 * Return '#ffffff' or '#111111' depending on which yields the better contrast
 * ratio against `bgHex`. Falls back to '#ffffff' for invalid input.
 * Uses WCAG 2.1 contrast formula (max ratio = 21).
 */
export function getReadableTextColor(bgHex) {
  const rgb = parseHexColor(bgHex);
  if (!rgb) return '#ffffff';
  const lumBg = relativeLuminance(rgb.r, rgb.g, rgb.b);
  // Luminance of pure white is 1, of #111 is ~0.0083.
  const lumWhite = 1;
  const lumDark = relativeLuminance(0x11, 0x11, 0x11);
  const contrastWhite = (lumWhite + 0.05) / (lumBg + 0.05);
  const contrastDark = (lumBg + 0.05) / (lumDark + 0.05);
  return contrastDark >= contrastWhite ? '#111111' : '#ffffff';
}

export function shadeColor(color, percent) {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);
  R = parseInt((R * (100 + percent)) / 100);
  G = parseInt((G * (100 + percent)) / 100);
  B = parseInt((B * (100 + percent)) / 100);
  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;
  const RR = (R.toString(16).length === 1 ? '0' : '') + R.toString(16);
  const GG = (G.toString(16).length === 1 ? '0' : '') + G.toString(16);
  const BB = (B.toString(16).length === 1 ? '0' : '') + B.toString(16);
  return '#' + RR + GG + BB;
}
